import type { Contact } from "@satyrsmc/shared/client";
import { formatAddress, getPrimaryAddress } from "./contactUtils";
import { formatPhoneNumber } from "@/lib/phone";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export type ContactTableColumn =
  "checkbox" | "name" | "phone" | "address" | "email" | "tags" | "status" | "actions";

export interface ContactTableRow {
  contact: Contact;
  canRemoveFromList?: boolean;
}

export interface ContactDirectoryTableProps {
  rows: ContactTableRow[];
  columns: ContactTableColumn[];
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: () => void;
  onRowClick?: (contact: Contact) => void;
  renderRowActions?: (row: ContactTableRow) => React.ReactNode;
}

export function ContactDirectoryTable({
  rows,
  columns,
  selectable = false,
  selectedIds = new Set(),
  onToggleSelect,
  onToggleSelectAll,
  onRowClick,
  renderRowActions,
}: ContactDirectoryTableProps) {
  const hasColumn = (col: ContactTableColumn) => columns.includes(col);
  const contacts = rows.map((r) => r.contact);

  return (
    <Table className="table-fixed min-w-[800px]">
      <TableHeader>
        <TableRow>
          {hasColumn("checkbox") && (
            <TableHead className="w-10 max-sm:py-3">
              {selectable && (
                <input
                  type="checkbox"
                  checked={selectedIds.size === contacts.length && contacts.length > 0}
                  onChange={onToggleSelectAll}
                  className="rounded"
                />
              )}
            </TableHead>
          )}
          {hasColumn("name") && <TableHead className="max-sm:py-3">Name</TableHead>}
          {hasColumn("phone") && <TableHead className="max-sm:py-3">Phone</TableHead>}
          {hasColumn("address") && <TableHead className="w-[200px] max-sm:py-3">Address</TableHead>}
          {hasColumn("email") && <TableHead className="max-sm:py-3">Email</TableHead>}
          {hasColumn("tags") && <TableHead className="max-sm:py-3">Tags</TableHead>}
          {hasColumn("status") && <TableHead className="max-sm:py-3">Status</TableHead>}
          {hasColumn("actions") && <TableHead className="w-12 max-sm:py-3" />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const c = row.contact;
          return (
            <TableRow
              key={c.id}
              className={onRowClick ? "cursor-pointer" : undefined}
              onClick={onRowClick ? () => onRowClick(c) : undefined}
            >
              {hasColumn("checkbox") && (
                <TableCell className="max-sm:py-3" onClick={(e) => e.stopPropagation()}>
                  {selectable && (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={() => onToggleSelect?.(c.id)}
                      className="rounded"
                    />
                  )}
                </TableCell>
              )}
              {hasColumn("name") && (
                <TableCell className="font-medium max-sm:py-3">{c.display_name}</TableCell>
              )}
              {hasColumn("phone") && (
                <TableCell className="text-muted-foreground max-sm:py-3">
                  {formatPhoneNumber(
                    c.phones?.find((p) => p.is_primary)?.phone ?? c.phones?.[0]?.phone ?? "",
                  ) || "—"}
                </TableCell>
              )}
              {hasColumn("address") && (
                <TableCell
                  className="text-muted-foreground overflow-hidden max-sm:py-3"
                  title={formatAddress(getPrimaryAddress(c)) || undefined}
                >
                  <span className="block truncate">
                    {formatAddress(getPrimaryAddress(c)) || "—"}
                  </span>
                </TableCell>
              )}
              {hasColumn("email") && (
                <TableCell className="text-muted-foreground max-sm:py-3">
                  {c.emails?.[0]?.email ?? "—"}
                </TableCell>
              )}
              {hasColumn("tags") && (
                <TableCell className="max-sm:py-3">
                  <div className="flex flex-wrap gap-1">
                    {(c.tags ?? []).slice(0, 3).map((t) => (
                      <span
                        key={t.id}
                        className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-xs"
                      >
                        {t.name}
                      </span>
                    ))}
                    {(c.tags ?? []).length > 3 && (
                      <span className="text-muted-foreground text-xs">
                        +{(c.tags ?? []).length - 3}
                      </span>
                    )}
                  </div>
                </TableCell>
              )}
              {hasColumn("status") && (
                <TableCell className="max-sm:py-3">
                  <span
                    className={`inline-flex rounded px-1.5 py-0.5 text-xs ${
                      c.status === "active"
                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                        : "bg-muted"
                    }`}
                  >
                    {c.status}
                  </span>
                </TableCell>
              )}
              {hasColumn("actions") && (
                <TableCell className="max-sm:py-3" onClick={(e) => e.stopPropagation()}>
                  {renderRowActions?.(row)}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
